// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import Success from "@synd/XDL/Icons/Check";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { xClassNames } from "@synd/XDL/Utilities";
import useValidatePasswordRequirements from "@synd/App/Hooks/validatePasswordRequirements";

type Props = {
    password: string;
    passwordMinimumLength: number;
    passwordRequiresDigit: boolean;
    passwordRequiresLowercase: boolean;
    passwordRequiresUppercase: boolean;
    passwordRequiresSpecialCharacter: boolean;
};

export interface PasswordRequirementsValidator {
    container?: InjectableCss;
    itemContainer?: InjectableCss;
    message?: TypographyPresentationProps;
    statusIcon?: IconPresentationProps;
}

export const passwordRequirementsValidatorStyles: PasswordRequirementsValidator = {};

const styles = passwordRequirementsValidatorStyles;

const PasswordRequirementWrapper: FC<{ password?: string; isPassedRequirement: boolean; message: string }> = ({
    password,
    isPassedRequirement,
    message,
}) => {
    const isPasswordEntered = !!password;
    const color = !isPasswordEntered ? "text" : isPassedRequirement ? "success" : "danger";
    return (
        <StyledWrapper {...styles.itemContainer}>
            <Icon
                src={isPassedRequirement ? Success : XCircle}
                {...styles.statusIcon}
                color={color}
                className={xClassNames.append(isPasswordEntered && "password-entered")}
            />
            <Typography {...styles.message} color={color}>
                {message}
            </Typography>
        </StyledWrapper>
    );
};

const PasswordRequirementsValidator = ({
    password,
    passwordMinimumLength,
    passwordRequiresDigit,
    passwordRequiresLowercase,
    passwordRequiresUppercase,
    passwordRequiresSpecialCharacter,
}: Props) => {
    const { hasLowerCase, hasUpperCase, hasNonAlphanumeric, hasDigit, hasExceededMinLength } =
        useValidatePasswordRequirements({
            password,
            passwordMinimumLength,
            passwordRequiresDigit,
            passwordRequiresLowercase,
            passwordRequiresUppercase,
            passwordRequiresSpecialCharacter,
        });

    return (
        <StyledWrapper {...styles.container} data-test-selector="password-requirements-validator">
            <PasswordRequirementWrapper
                password={password}
                isPassedRequirement={hasExceededMinLength}
                message={translate("Password must be at least {0} characters long", passwordMinimumLength.toString())}
            />
            {passwordRequiresDigit && (
                <PasswordRequirementWrapper
                    password={password}
                    isPassedRequirement={hasDigit}
                    message={translate("Password must include at least one number")}
                />
            )}
            {passwordRequiresLowercase && (
                <PasswordRequirementWrapper
                    password={password}
                    isPassedRequirement={hasLowerCase}
                    message={translate("Password must include at least one lowercase character")}
                />
            )}
            {passwordRequiresUppercase && (
                <PasswordRequirementWrapper
                    password={password}
                    isPassedRequirement={hasUpperCase}
                    message={translate("Password must include at least one uppercase character")}
                />
            )}
            {passwordRequiresSpecialCharacter && (
                <PasswordRequirementWrapper
                    password={password}
                    isPassedRequirement={hasNonAlphanumeric}
                    message={translate("Password must include at least one non alphanumeric character")}
                />
            )}
        </StyledWrapper>
    );
};

export default PasswordRequirementsValidator;
